import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseMutationFn, useSupabaseTypedClient } from 'hooks/reactQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import { EdgeFunctionName } from 'types/edgeFunctions';
import { TanstackQueryName } from 'types/frontend';

interface RedeemTokenRewardParams {
  rewardType: 'increase-storage' | 'extend-ai-limit';
}

interface RedeemTokenRewardResponse {
  message: string;
  newValue: number;
}

export const useRedeemTokenRewardMutation = () => {
  const supabase = useSupabaseTypedClient();
  const queryClient = useQueryClient();
  const { id: userId } = useCurrentUser();
  const { toastSuccess, toastError } = useToastWrapper();

  return useMutation<RedeemTokenRewardResponse, string, RedeemTokenRewardParams>({
    mutationFn: supabaseMutationFn(({ rewardType }) =>
      supabase.functions.invoke<RedeemTokenRewardResponse>(EdgeFunctionName.RedeemTokenReward, {
        body: { rewardType } as RedeemTokenRewardParams,
      })
    ),
    onSuccess: (_, { rewardType }) => {
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetCurrentUser, userId] });
      queryClient.invalidateQueries({ queryKey: [TanstackQueryName.GetUserAssets, userId] });
      toastSuccess(
        rewardType === 'increase-storage'
          ? 'Storage increased by 5 MB'
          : 'AI limit extended by 50K tokens'
      );
    },
    onError: (error) => {
      toastError(error || 'Failed to redeem reward');
    },
  });
};
